import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {useQuery, useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
import {ErrorState} from 'shells/error-state/error-state';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Table} from 'shells/table/table';
import {HeaderWithInfo} from 'shells/table/table-header/header-with-info';

import type {NotifiableAliasType} from 'nutshell-graphql-types';
import type {
    GetEmailTemplateLinkEngagementStats as GetEmailTemplateLinkEngagementStatsQuery,
    GetEmailTemplateLinkEngagementStatsVariables as GetEmailTemplateLinkEngagementStatsQueryVariables,
    SetClickNotification as SetClickNotificationMutation,
    SetClickNotificationVariables as SetClickNotificationMutationVariables,
} from 'nutshell-graphql-types';

import {updateSearchWithMultipleNewFilters} from '../../utils';

import {getEditionIdFromApiId} from '../recipient-list/helpers';

import SET_CLICK_NOTIFICATION from './graphql/mutations/click-notification-set.graphql';
import GET_EMAIL_TEMPLATE_LINK_ENGAGEMENT_STATS from './graphql/queries/get-email-template-link-engagement-stats.graphql';
import {CustomNotificationCell} from './link-tracking-notifications/custom-notification-cell';

import styles from './edition-link-tracking-table.module.css';

type Props = {
    editionId: string;
    emailTemplateId: string;
    canEditNotifiables: boolean;
    shouldShowCounts: boolean;
};

type EmailTemplateLink = {
    link: string;
    url?: {
        id: string;
        fullyQualifiedUrl: string;
    };
    numTimesSent: number;
    numTotalClicks: number;
    numUniqueClicks: number;
    notifiables?: any | null | undefined;
};

export function EditionLinkTrackingTable(props: Props) {
    const {emailTemplateId, canEditNotifiables, shouldShowCounts, editionId} = props;

    const {data, loading, error, refetch} = useQuery<
        GetEmailTemplateLinkEngagementStatsQuery,
        GetEmailTemplateLinkEngagementStatsQueryVariables
    >(GET_EMAIL_TEMPLATE_LINK_ENGAGEMENT_STATS, {
        variables: {emailTemplateId: emailTemplateId},
        fetchPolicy: 'cache-and-network',
    });

    const [setClickNotification] = useMutation<
        SetClickNotificationMutation,
        SetClickNotificationMutationVariables
    >(SET_CLICK_NOTIFICATION);

    const emailTemplateLinks = data && data.emailTemplate ? data.emailTemplate.links : [];

    const hasNoLinks = !loading && !emailTemplateLinks.length;

    // If the wild card link doesn't have any notifiables, it won't come
    // back in our query. This adds the wild card link to the array of links if it's
    // not already there so it's visible in the table and notifiables can be added.
    // If we aren't allowing editing notifiables, we sort the links so the wild card
    // shows up first on the table.
    if (
        canEditNotifiables &&
        !hasNoLinks &&
        emailTemplateLinks.filter((link) => link.link === '*').length === 0
    ) {
        const anyLink = {
            link: '*',
            url: null,
            numTimesSent: 0,
            numTotalClicks: 0,
            numUniqueClicks: 0,
            notifiables: [],
            __typename: 'EmailTemplateLink',
        } as const;

        // @ts-expect-error
        emailTemplateLinks.unshift(anyLink);
    } else {
        const anyLinkValue = '*';

        emailTemplateLinks.sort((link) => {
            return link.link === anyLinkValue ? -1 : 0;
        });
    }

    const getTotal = (rows: any[], clickType: string) => {
        return rows
            .filter((row) => row.values.link.link !== '*')
            .reduce((sum, row) => {
                const value =
                    clickType === 'uniqueClicks'
                        ? row.values[clickType].numUniqueClicks
                        : row.values[clickType];

                return value + sum;
            }, 0)
            .toLocaleString('en-US');
    };

    const columns = React.useMemo(
        () => [
            {
                Header: 'Link',
                id: 'link',
                accessor: (templateLink: EmailTemplateLink) => templateLink,
                dataType: 'component',
                minWidth: 175,
                width: 175,
                maxWidth: 225,
                disableSortBy: true,
                hasRightBorder: true,
                hasLeftBorder: true,
                shouldTruncate: true,
                Footer: 'Totals',
                Cell: ({cell}: any) => {
                    if (cell.value.link === '*') {
                        return <div>Any link in this message</div>;
                    }

                    return (
                        <Link href={cell.value.link} variant='primary' size='small' newTab={true}>
                            {cell.value.link}
                        </Link>
                    );
                },
            },
            {
                Header: () => (
                    <HeaderWithInfo
                        title='Total clicks'
                        infoText='The # of total clicks on this link. If a single recipient clicks this link 7 times, it will count as 7 total clicks.'
                    />
                ),
                id: 'totalClicks',
                accessor: (templateLink: EmailTemplateLink) => {
                    if (templateLink.link === '*') {
                        return '';
                    }

                    if (!shouldShowCounts) {
                        return '-';
                    }

                    return templateLink.numTotalClicks;
                },
                dataType: 'integer',
                minWidth: 90,
                width: 90,
                maxWidth: 100,
                // @ts-expect-error
                Footer: (tableData) =>
                    !shouldShowCounts ? '-' : getTotal(tableData.rows, 'totalClicks'),
            },
            {
                Header: () => (
                    <HeaderWithInfo
                        title='Unique clicks'
                        infoText=' The # of unique recipients that clicked this link. If a single recipient clicked this link 5 times, it will only count as 1 unique click.'
                    />
                ),
                id: 'uniqueClicks',
                accessor: (templateLink: EmailTemplateLink) => templateLink,
                Cell: ({cell}: any) => {
                    if (cell.value.link === '*') {
                        return '';
                    }

                    if (!shouldShowCounts) {
                        return '-';
                    }

                    if (
                        cell.value.url &&
                        NutClientConfig.hasMarketingProLinkReportFF &&
                        NutClientConfig.hasLinkReportFeature
                    ) {
                        const newFilterKey = `clickedEditionUrls_${getEditionIdFromApiId(
                            editionId
                        )}`;

                        const updatedSearch = updateSearchWithMultipleNewFilters(
                            [
                                {filterKey: 'clickedEditions', entityId: editionId},
                                {filterKey: newFilterKey, entityId: cell.value.url.id},
                            ],
                            location.search,
                            ['clickedEditions', newFilterKey]
                        );

                        return (
                            <Link
                                // @ts-expect-error
                                as={RoutedLink}
                                to={`recipients?${updatedSearch}`}
                                variant='primary'
                                size='small'
                            >
                                {cell.value.numUniqueClicks}
                            </Link>
                        );
                    }

                    return cell.value.numUniqueClicks;
                },
                dataType: 'integer',
                minWidth: 90,
                width: 90,
                maxWidth: 100,
                hasRightBorder: true, // Remove this once the Notifications column is live for all users
                // @ts-expect-error
                Footer: (tableData) =>
                    !shouldShowCounts ? '-' : getTotal(tableData.rows, 'uniqueClicks'),
            },
            {
                Header: () => (
                    <HeaderWithInfo
                        title='Notifications'
                        infoText='Notify users when a link is clicked'
                    />
                ),
                id: 'notifications',
                accessor: (templateLink: EmailTemplateLink) => templateLink,
                dataType: 'component',
                minWidth: 200,
                width: 200,
                maxWidth: 250,
                disableSortBy: true,
                hasRightBorder: true,
                Cell: ({cell}: any) => {
                    return (
                        <CustomNotificationCell
                            onSetNotification={(
                                userIds: string[],
                                assigneeValues: NotifiableAliasType[]
                            ) => {
                                setClickNotification({
                                    variables: {
                                        input: {
                                            emailTemplateId,
                                            link: cell.value.link,
                                            notifiables: {
                                                entityIds: userIds,
                                                notifiableAliasTypes: assigneeValues,
                                            },
                                        },
                                    },
                                }).then(() =>
                                    // this timeout times the refetch to show the updates
                                    // when the button turns to 'success'
                                    setTimeout(() => {
                                        refetch();
                                    }, 1)
                                );
                            }}
                            notifiableUsers={
                                cell.value.notifiables
                                    ? // @ts-expect-error
                                      cell.value.notifiables.filter((notifiable) => notifiable.id)
                                    : []
                            }
                            notifiableAssignees={
                                cell.value.notifiables
                                    ? // @ts-expect-error
                                      cell.value.notifiables.filter((notifiable) => notifiable.type)
                                    : []
                            }
                            shouldAllowEditingNotifiables={canEditNotifiables}
                        />
                    );
                },
            },
        ],
        [
            shouldShowCounts,
            canEditNotifiables,
            emailTemplateId,
            refetch,
            setClickNotification,
            editionId,
        ]
    );

    return (
        <div className={styles['edition-link-tracking-table-container']}>
            <Table
                isResizable={true}
                hasHeaderBorder={true}
                hasHeaderTopBorder={true}
                hasHeaderLeftBorder={true}
                hasHeaderRightBorder={true}
                hasHeaderCellBorders={true}
                hasBorders={true}
                isCompact={true}
                disableFilters={true}
                // @ts-expect-error
                columns={columns}
                data={emailTemplateLinks}
                shouldShowFooter={!hasNoLinks}
                customCellFunctions={{
                    setClickNotification: setClickNotification,
                }}
                specialStates={[
                    {
                        // Error state
                        shouldRender: () => Boolean(error),
                        component: (
                            // @ts-expect-error
                            <ErrorState errorMessage={safelyGetGraphQLErrorMessage(error)} />
                        ),
                    },
                    {
                        // Loading state
                        shouldRender: () => loading,
                        component: <LoadingPage />,
                    },
                    {
                        // Empty state
                        shouldRender: () => hasNoLinks,
                        component: shouldShowCounts ? (
                            <NothingFoundEmptyState
                                title='This message wasn’t sent with any links'
                                subtitle='Add links to message content to see them here'
                            />
                        ) : (
                            <NothingFoundEmptyState
                                title='This message doesn’t have any links yet'
                                subtitle='Add links to your message content to see them here'
                            />
                        ),
                    },
                ]}
            />
        </div>
    );
}
